import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from './lib/cn';

export interface DropdownItem {
  id: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
  tone?: 'default' | 'danger';
  separatorBefore?: boolean;
  onSelect: () => void;
}

export interface DropdownProps {
  trigger: ReactNode;
  items: DropdownItem[];
  ariaLabel: string;
  align?: 'start' | 'end';
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  menuClassName?: string;
}

export function Dropdown({
  trigger,
  items,
  ariaLabel,
  align = 'start',
  disabled = false,
  className,
  triggerClassName,
  menuClassName,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuId = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());

  const enabledItems = useMemo(() => items.filter((item) => !item.disabled), [items]);

  const closeMenu = useCallback((restoreFocus = true) => {
    setIsOpen(false);
    if (restoreFocus) window.setTimeout(() => triggerRef.current?.focus(), 0);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const firstItem = enabledItems[0];
    const focusTimer = window.setTimeout(() => {
      if (firstItem) itemRefs.current.get(firstItem.id)?.focus();
    }, 0);

    const handlePointerDown = (event: PointerEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) closeMenu(false);
    };
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeMenu, enabledItems, isOpen]);

  const focusItem = (currentId: string, direction: 1 | -1) => {
    const currentIndex = enabledItems.findIndex((item) => item.id === currentId);
    if (currentIndex < 0 || enabledItems.length === 0) return;
    const nextIndex = (currentIndex + direction + enabledItems.length) % enabledItems.length;
    const nextItem = enabledItems[nextIndex];
    if (nextItem) itemRefs.current.get(nextItem.id)?.focus();
  };

  const handleItemKeyDown = (event: KeyboardEvent<HTMLButtonElement>, item: DropdownItem) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusItem(item.id, 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusItem(item.id, -1);
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const nextItem =
        event.key === 'Home' ? enabledItems[0] : enabledItems[enabledItems.length - 1];
      if (nextItem) itemRefs.current.get(nextItem.id)?.focus();
    }
  };

  return (
    <div ref={wrapperRef} className={cn('relative inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        disabled={disabled}
        onClick={() => setIsOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            setIsOpen(true);
          }
        }}
        className={cn(
          'inline-flex min-h-11 items-center justify-center gap-2 rounded-[13px] border border-transparent px-3 text-sm font-extrabold text-[var(--foreground)] transition-[background-color,color,border-color] duration-200 hover:border-[var(--border)] hover:bg-[var(--surface-raised)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)] disabled:pointer-events-none disabled:opacity-45',
          triggerClassName,
        )}
      >
        {trigger}
      </button>

      {isOpen && (
        <div
          id={menuId}
          role="menu"
          aria-label={ariaLabel}
          className={cn(
            'motion-scale-in absolute top-[calc(100%+0.5rem)] z-50 min-w-56 overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-1.5 shadow-[var(--shadow-sm)]',
            align === 'end' ? 'right-0 origin-top-right' : 'left-0 origin-top-left',
            menuClassName,
          )}
        >
          {items.map((item) => (
            <div key={item.id} role="none">
              {item.separatorBefore && (
                <div role="separator" className="my-1.5 border-t border-[var(--border)]" />
              )}
              <button
                ref={(node) => {
                  if (node) itemRefs.current.set(item.id, node);
                  else itemRefs.current.delete(item.id);
                }}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  item.onSelect();
                  closeMenu();
                }}
                onKeyDown={(event) => handleItemKeyDown(event, item)}
                className={cn(
                  'flex min-h-11 w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors duration-150 hover:bg-[var(--surface-raised)] focus-visible:bg-[var(--surface-raised)] focus-visible:outline-none disabled:pointer-events-none disabled:opacity-45',
                  item.tone === 'danger' ? 'text-[var(--danger)]' : 'text-[var(--foreground)]',
                )}
              >
                {item.icon && (
                  <span className="shrink-0 text-[var(--muted-foreground)]" aria-hidden="true">
                    {item.icon}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block text-sm font-extrabold">{item.label}</span>
                  {item.description && (
                    <span className="mt-0.5 block text-xs leading-4 text-[var(--muted-foreground)]">
                      {item.description}
                    </span>
                  )}
                </span>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
